import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { adminDashboardService } from 'api/adminDashboardService';
import { TrendingUp, TrendingDown, Payments, AccountBalance } from '@mui/icons-material';
import { format } from 'date-fns';

export default function SystemFinancialReport() {
    const {
        data: salesReport,
        isLoading,
        isError
    } = useQuery({
        queryKey: ['adminSalesReport'],
        queryFn: () => adminDashboardService.getSalesReport()
    });

    if (isLoading)
        return (
            <Box p={5} display="flex" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    if (isError || !salesReport)
        return (
            <Box p={5}>
                <Alert severity="error">Failed to load platform sales report</Alert>
            </Box>
        );

    const { summary, timeline, byCategory } = salesReport;

    return (
        <Box p={3} sx={{ bgcolor: '#fbfbfb', minHeight: '100vh' }}>
            <Box mb={4}>
                <Typography variant="h4" fontWeight={900}>
                    Platform Financial Report
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Global overview of all platform earnings and sales volume
                </Typography>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 3, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ p: 1.5, bgcolor: 'primary.light', borderRadius: 2, color: 'primary.main' }}>
                            <TrendingUp />
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                TOTAL REVENUE
                            </Typography>
                            <Typography variant="h5" fontWeight={800}>
                                ₦{(summary?.totalRevenue || 0).toLocaleString()}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 3, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ p: 1.5, bgcolor: 'success.light', borderRadius: 2, color: 'success.main' }}>
                            <AccountBalance />
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                NET EARNINGS
                            </Typography>
                            <Typography variant="h5" fontWeight={800}>
                                ₦{(summary?.netEarnings || 0).toLocaleString()}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 3, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ p: 1.5, bgcolor: 'info.light', borderRadius: 2, color: 'info.main' }}>
                            <Payments />
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                TOTAL ORDERS
                            </Typography>
                            <Typography variant="h5" fontWeight={800}>
                                {summary?.totalOrders || 0}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Recent Timeline */}
            <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 4 }}>
                <Box p={2} sx={{ bgcolor: '#fafafa', borderBottom: '1px solid #eee' }}>
                    <Typography variant="h6" fontWeight={700}>
                        Sales Timeline (Last 30 Days)
                    </Typography>
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Orders</TableCell>
                                <TableCell>Gross Volume</TableCell>
                                <TableCell>Platform Earnings</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {timeline && timeline.length > 0 ? (
                                timeline.map((item, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>{item.date}</TableCell>
                                        <TableCell>{item.orders}</TableCell>
                                        <TableCell fontWeight={600}>₦{(item.gross || 0).toLocaleString()}</TableCell>
                                        <TableCell sx={{ color: 'success.main', fontWeight: 700 }}>
                                            ₦{(item.earnings || 0).toLocaleString()}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        No recent data
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
}
